import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useId, useState } from 'react';
import { hasContentPreferences } from '../../../../../utils/hasContentPreferences';
import { BusinessInfoDisplay } from '../../../../components/BusinessInfoDisplay';
import { Button } from '../../../../components-v2/Button';
import { Checkbox } from '../../../../components-v2/Checkbox';
import { styleSheet } from './styles';

const CONTENT_TYPE_OPTIONS = [
	{ label: 'Email', value: 'Email' },
	{ label: 'Social Media', value: 'SocialMediaPost' },
	{ label: 'Blog', value: 'Blog' },
] as const;

const MAX_INSTRUCTIONS_LENGTH = 2000;

function getAvailableContentTypes(accountFeatures?: Partial<Api.IAccountFeatures>) {
	return CONTENT_TYPE_OPTIONS.filter(option => {
		if (option.value === 'SocialMediaPost') return accountFeatures?.socialMedia?.enabled !== false;
		if (option.value === 'Blog') return accountFeatures?.blogFeature?.enabled !== false;
		return true;
	});
}

interface ContentCalendarGenerationFormProps {
	onGenerate: (contentTypes: string[], instructions: string) => void;
	disabled?: boolean;
	contentProfile?: Api.AccountProfile | null;
	accountFeatures?: Partial<Api.IAccountFeatures>;
}

export function ContentCalendarGenerationForm({
	onGenerate,
	disabled,
	contentProfile,
	accountFeatures,
}: ContentCalendarGenerationFormProps) {
	const availableOptions = getAvailableContentTypes(accountFeatures);
	const [selectedTypes, setSelectedTypes] = useState<string[]>(() => availableOptions.map(o => o.value));
	const [instructions, setInstructions] = useState('');
	const [preferencesExpanded, setPreferencesExpanded] = useState(false);
	const checkboxId = useId();
	const hasPreferences = hasContentPreferences(contentProfile);

	const toggleType = (value: string) => {
		setSelectedTypes(prev => (prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]));
	};

	const handleSubmit = () => {
		if (selectedTypes.length === 0) return;
		onGenerate(selectedTypes, instructions);
	};

	return (
		<div className={css(styleSheet.generationForm)}>
			<div className={css(styleSheet.contentTypeCheckboxes)}>
				{availableOptions.map(option => {
					const id = `${checkboxId}-${option.value}`;
					return (
						<div key={option.value} className={css(styleSheet.contentTypeCheckboxRow)}>
							<Checkbox
								id={id}
								checked={selectedTypes.includes(option.value)}
								onCheckedChange={() => toggleType(option.value)}
								disabled={disabled}
							/>
							<label htmlFor={id} className={css(styleSheet.contentTypeLabel)}>
								{option.label}
							</label>
						</div>
					);
				})}
			</div>

			{hasPreferences ? (
				<div className={css(styleSheet.preferencesSection)}>
					<button
						type='button'
						className={css(styleSheet.preferencesToggle)}
						onClick={() => setPreferencesExpanded(prev => !prev)}
						aria-expanded={preferencesExpanded}
					>
						<span>Your content preferences</span>
						<span
							className={css(
								styleSheet.preferencesChevron,
								preferencesExpanded && styleSheet.preferencesChevronExpanded
							)}
						>
							&#9662;
						</span>
					</button>
					{preferencesExpanded ? (
						<div className={css(styleSheet.preferencesContent)}>
							<BusinessInfoDisplay profile={contentProfile} />
						</div>
					) : null}
				</div>
			) : null}

			<div className={css(styleSheet.instructionsSection)}>
				<label className={css(styleSheet.instructionsLabel)}>
					{hasPreferences ? 'Any additional instructions?' : 'Any special directions for this content?'}
				</label>
				<textarea
					className={css(styleSheet.instructionsTextarea)}
					value={instructions}
					onChange={e => setInstructions(e.target.value)}
					placeholder={
						hasPreferences
							? 'e.g. "This client wants a humorous tone" or "Focus on spring promotions"'
							: 'e.g. "Focus on spring promotions" or "Keep the tone warm and approachable"'
					}
					maxLength={MAX_INSTRUCTIONS_LENGTH}
					disabled={disabled}
					rows={3}
				/>
			</div>

			<Button onClick={handleSubmit} disabled={disabled || selectedTypes.length === 0}>
				Generate Content Plan
			</Button>
		</div>
	);
}
